import { Component, Input, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { CashierService } from 'src/app/services/cashier.service';
import { ResponseObject } from 'src/app/models/response-object.model';
import { ToastrService } from 'ngx-toastr';
import { AccountService } from 'src/app/services/account.service';
import { WarehouseService } from 'src/app/services/warehouse.service';
import { AuthenticationService } from '../../authentication/authentication/authentication.service';
import { BankTransferAdministrativeLogService } from 'src/app/services/bank-transfer-administrative-log.service';

interface WarehouseResponse {
  id: number;
  name: string;
}

interface CashResponse {
  id: number;
  name: string;
  description?: string;
}

interface BankAccountResponse {
  id: number;
  bank?: number;
  bankName?: string;
  name?: string;
  accountNumber: string;
}

interface DepositRequestResponse {
    bankAccountId: number | null;
    cashAccId: number;
    company: number;
    warehouse: number;
    bankName: string;
    accountNumber: string;
    accountAlias: string;
    amount: number;
}


interface TransferDetailRequest {
  currency: number;
  amount: number;
  document: string | null;
  externalReference: number | null;
  externalReferenceType: number;
}

interface LocalTransferRequest {
  from: number;
  to: number;
  issuerCash: number | null;
  issuerBank: number | null;
  receivingCash: number | null;
  receivingBank: number | null;
  date: number;
  description: string | null;
  image: string | null;
  docNumber: string | null;
  exchangeRate: number;
  rollback: boolean;
  details: TransferDetailRequest[];
}

interface LocalTransferDualRequest {
  transferAdmin: LocalTransferRequest;
  transferFiscal: LocalTransferRequest;
  companyFiscal: number;
}

@Component({
  selector: 'app-deposit-request',
  templateUrl: './deposit-request.component.html',
  styleUrls: ['./deposit-request.component.scss']
})
export class DepositRequestComponent implements OnInit {
  loading = false;
  saving = false;
  loadingPermissions = true;
  selectedDate: string = this.getToday();
  selectedWarehouseId: number | null = null;
  warehouses: WarehouseResponse[] = [];
  permittedWarehouses: number[] = [];
  permittedCashRegisters: number[] = [];
  deposits: DepositRequestResponse[] = [];
  selectedDeposit: DepositRequestResponse | null = null;
  cashiers: CashResponse[] = [];
  bankAccounts: BankAccountResponse[] = [];
  selectedCashId: number | null = null;
  selectedBankAccountId: number | null = null;
  showDepositModal = false;
  amountQ: number | null = null;
  reference = '';
  description = '';
  formDate: string = this.getToday();
  imageBase64: string | null = null;
  imagePreview: string | null = null;

  constructor(
      private toastService: ToastrService,
      private cashierService: CashierService,
      private accountService: AccountService,
      private warehouseService: WarehouseService,
      private authService: AuthenticationService,
      private bankTransferAdministrativeLogService: BankTransferAdministrativeLogService,
  ) {
    
  }

  ngOnInit(): void {
    this.loadCashBanks();
    this.loadWarehouses();
    this.loadPermissions();
  }

  private loadPermissions(): void {
    const currentUserPosition = this.authService.getUserPosition();
    if (!currentUserPosition) {
        return;
    }

    const warehouseIds = new Set<number>();

    currentUserPosition.forEach(position => {
        if (
            position.warehouse != null &&
            Number(position.warehouse) > 0
        ) {
            warehouseIds.add(Number(position.warehouse));
        }
    });

    this.permittedWarehouses = Array.from(warehouseIds);

    const cashIds = new Set<number>();

    currentUserPosition.forEach(position => {
        if (
            position.cash != null &&
            Number(position.cash) > 0
        ) {
            cashIds.add(Number(position.cash));
        }
    });

    this.permittedCashRegisters = Array.from(cashIds);

    this.filterAvailableWarehouses();
    this.filterAvailableCashRegisters();
  }

  private loadCashBanks(): void {

      this.loadCashRegisters();
      this.loadBankAccounts();
  }

  private loadCashRegisters(): void {
    this.cashierService.getCashiersByClue('').subscribe(
      {
        next: (response: ResponseObject) => {
          this.cashiers = response.data;
          this.filterAvailableCashRegisters();
        },
        error: (error: HttpErrorResponse) => {
          this.toastService.error("Error al obtener las areas, por favor recarga la página");
        }
      }
    )
  }

  private loadBankAccounts(): void {
    this.accountService.getAccounts().subscribe({
      next: (response: ResponseObject) => {
        this.bankAccounts = response.data;
      },
      error: (error: HttpErrorResponse) => {
        this.toastService.error("Error traer las cuentas");
      }
    })
  }

  private loadWarehouses(): void {
    this.warehouseService.getWarehouseByClue('').subscribe({
      next: (response: ResponseObject) => {
        this.warehouses = response.data;
        this.filterAvailableWarehouses();
      },
      error: (error: HttpErrorResponse) => {
        this.toastService.error(error.error.message);
      }
    });
  }

  private filterAvailableWarehouses(): void {

    if (this.canViewAllWarehousesAndCash()) {
        return;
    }

    if (!this.warehouses?.length) {
        return;
    }

    this.warehouses = this.warehouses.filter(
        warehouse =>
            this.permittedWarehouses.includes(
                Number(warehouse.id)
            )
    );
  }

  private filterAvailableCashRegisters(): void {

    if (this.canViewAllWarehousesAndCash()) {
        return;
    }

    if (!this.cashiers?.length) {
        return;
    }

    this.cashiers = this.cashiers.filter(
        cash =>
            this.permittedCashRegisters.includes(
                Number(cash.id)
            )
    );
  }

  loadDeposits(): void {
    if (!this.selectedWarehouseId) {

        this.toastService.warning(
            'Seleccione una bodega'
        );

        return;
    }

    if (!this.selectedDate) {

        this.toastService.warning(
            'Seleccione una fecha'
        );

        return;
    }

    this.loading = true;

    const milliseconds =
        this.getStartOfDayMilliseconds(
            this.selectedDate
        );

    console.log(
        'Fecha enviada:',
        milliseconds
    );

    console.log(
        'Bodega enviada:',
        this.selectedWarehouseId
    );

    this.bankTransferAdministrativeLogService
        .getDepositRequestsApp(
            milliseconds,
            this.selectedWarehouseId
        )
        .pipe(
            finalize(() => {
                this.loading = false;
            })
        )
        .subscribe({

            next: (response: ResponseObject) => {

                this.deposits = response.data || [];

                console.log(
                    'Deposits:',
                    this.deposits
                );
            },

            error: error => {

                console.error(
                    'Error al obtener depósitos:',
                    error
                );

                this.toastService.error(
                    error.error?.message ||
                    'Error al obtener los depósitos'
                );
            }

        });
  }

  openDepositForm(
      deposit: DepositRequestResponse
  ): void {

      // Los depositos con idBank = 0 no se pueden abrir

      if (deposit.bankAccountId == null) {
          return;
      }

      this.selectedDeposit = deposit;

      this.amountQ =
          Number(deposit.amount || 0);

      this.reference = '';
      this.description = '';

      this.formDate =
          this.getToday();

      this.selectedCashId = null;

      this.imageBase64 = null;
      this.imagePreview = null;

      const bankAccount =
          this.bankAccounts.find(account =>
              String(account.accountNumber).trim() ===
              String(deposit.accountNumber).trim()
          );

      this.selectedBankAccountId =
          bankAccount?.id ?? null;

      this.showDepositModal = true;
  }

  closeDepositModal(): void {

      if (this.saving) {
          return;
      }

      this.showDepositModal = false;

      this.selectedDeposit = null;

      this.cleanTransferForm();
  }

  private cleanTransferForm(): void {

      this.selectedCashId = null;
      this.selectedBankAccountId = null;

      this.amountQ = null;

      this.reference = '';
      this.description = '';

      this.formDate =
          this.getToday();

      this.imageBase64 = null;
      this.imagePreview = null;
  }

  // =========================================================
  // IMAGEN
  // =========================================================

  onImageSelected(event: Event): void {

      const input =
          event.target as HTMLInputElement;

      if (
          !input.files ||
          input.files.length === 0
      ) {
          return;
      }

      const file = input.files[0];

      if (!file.type.startsWith('image/')) {

          this.toastService.warning(
              'Seleccione un archivo de imagen válido'
          );

          input.value = '';

          return;
      }

      const reader = new FileReader();

      reader.onload = () => {

          const result =
              reader.result as string;

          /**
           * Para mostrarla en el navegador.
           */
          this.imagePreview = result;

          /**
           * Flutter envía únicamente el contenido Base64,
           * sin:
           *
           * data:image/png;base64,
           */
          this.imageBase64 =
              result.includes(',')
                  ? result.split(',')[1]
                  : result;
      };

      reader.onerror = () => {

          this.toastService.error(
              'No fue posible leer la imagen'
          );
      };

      reader.readAsDataURL(file);
  }

  removeImage(): void {
      this.imageBase64 = null;
      this.imagePreview = null;
  }

  saveFormData(): void {
    const dep = this.selectedDeposit;

    if (!dep) {
        return;
    }

    if (!this.selectedCashId) {

        this.toastService.warning(
            'Seleccione una caja'
        );

        return;
    }

    if (!this.selectedBankAccountId) {

        this.toastService.warning(
            'Seleccione un banco'
        );

        return;
    }

    if (
        this.amountQ == null ||
        Number(this.amountQ) <= 0
    ) {

        this.toastService.warning(
            'Ingrese un monto mayor a cero'
        );

        return;
    }

    if (!this.formDate) {

        this.toastService.warning(
            'Seleccione una fecha'
        );

        return;
    }

    const startOfDay =
        this.getStartOfDayMilliseconds(
            this.formDate
        );

    const requestAdmin: LocalTransferRequest = {

        from: 2,
        to: 1,

        issuerCash:
            this.selectedCashId,

        issuerBank:
            null,

        receivingCash:
            null,

        receivingBank:
            this.selectedBankAccountId,

        date:
            startOfDay,

        description:
            this.description?.trim() || null,

        image:
            this.imageBase64,

        docNumber:
            this.reference?.trim() || null,

        exchangeRate:
            1,

        rollback:
            false,

        details: []
    };

    requestAdmin.details.push({

        currency: 0,

        amount:
            Number(this.amountQ),

        document:
            requestAdmin.docNumber,

        externalReference:
            requestAdmin.issuerCash,

        externalReferenceType:
            1
    });

    const requestFiscal: LocalTransferRequest = {

        from: 2,
        to: 1,

        issuerCash:
            dep.cashAccId,

        issuerBank:
            null,

        receivingCash:
            null,

        receivingBank:
            dep.bankAccountId,

        date:
            startOfDay,

        description:
            this.description?.trim() || null,

        image:
            this.imageBase64,

        docNumber:
            this.reference?.trim() || null,

        exchangeRate:
            1,

        rollback:
            false,

        details: []
    };

    requestFiscal.details.push({

        currency: 0,

        amount:
            Number(this.amountQ),

        document:
            requestFiscal.docNumber,

        externalReference:
            requestFiscal.issuerCash,

        externalReferenceType:
            1
    });

    const dualRequest: LocalTransferDualRequest = {

        transferAdmin:
            requestAdmin,

        transferFiscal:
            requestFiscal,

        companyFiscal:
            dep.company
    };

    console.log(
        'DEPÓSITO SELECCIONADO:',
        dep
    );

    console.log(
        'REQUEST ADMIN:',
        requestAdmin
    );

    console.log(
        'REQUEST FISCAL:',
        requestFiscal
    );

    console.log(
        'REQUEST DUAL:',
        dualRequest
    );

    this.saveTransferCashBank(
        dualRequest
    );
  }

  private saveTransferCashBank(
      request: LocalTransferDualRequest
  ): void {
    if (this.saving) {
        return;
    }
    this.saving = true;

    this.bankTransferAdministrativeLogService
        .saveTransferCashBank(
            request
        )
        .pipe(
            finalize(() => {
                this.saving = false;
            })
        )
        .subscribe({

            next: () => {

                this.toastService.success(
                    'Traslado realizado con éxito'
                );

                this.showDepositModal = false;

                this.selectedDeposit = null;

                this.cleanTransferForm();

                this.loadDeposits();
            },

            error: error => {

                console.error(
                    'Error guardando traslado:',
                    error
                );

                this.toastService.error(
                    error.error?.message ||
                    'Error al guardar el traslado'
                );
            }

        });
  }

  isCashDeposit(
      deposit: DepositRequestResponse
  ): boolean {

      return deposit.bankAccountId == null;
  }

  getDepositTitle(
      deposit: DepositRequestResponse
  ): string {

      if (this.isCashDeposit(deposit)) {
          return deposit.bankName;
      }

      return `${deposit.bankName} - No. ${deposit.accountNumber}`;
  }

  getCashName(
      cash: CashResponse
  ): string {

      return cash.name ||
          cash.description ||
          `Caja ${cash.id}`;
  }

  getBankName(
      account: BankAccountResponse
  ): string {

      const bankName =
          account.bankName ||
          account.name ||
          'Banco';

      return `${bankName} - ${account.accountNumber}`;
  }

  trackByDeposit(
      index: number,
      deposit: DepositRequestResponse
  ): string {

      return `${deposit.company}-${deposit.warehouse}-${deposit.cashAccId}-${deposit.bankAccountId ?? 'cash'}`;
  }

  private getToday(): string {
      const now = new Date();

      const year = now.getFullYear();

      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
  }

  private getStartOfDayMilliseconds(
      value: string
  ): number {
      const parts = value.split('-');
      const year = Number(parts[0]);
      const month = Number(parts[1]);
      const day = Number(parts[2]);
      const date = new Date(year,month - 1,day,0,0,0,0);
      return date.getTime();
  }

  private canViewAllWarehousesAndCash(): boolean {
    const user = this.authService.getUser();
    const email = user?.mail
        ?.trim()
        ?.toLowerCase();

    if (!email) {
        return false;
    }
    return (
        email.endsWith('@servicios.gt') ||
        email.endsWith('@impulsocomercial.gt')
    );
  }

}